import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  MenuItem,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";

interface UpdateBankAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { holderName: string; bankName: string; accountNumber: string; routingNumber: string; type: string }) => void;
}

const ACCOUNT_TYPES = ["Checking", "Savings"];

const UpdateBankAccountDialog: React.FC<UpdateBankAccountDialogProps> = ({ open, onClose, onSave }) => {
  const [holderName, setHolderName] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountType, setAccountType] = useState("Checking");

  const handleSave = () => {
    if (!holderName || !bankName || !accountNumber || !routingNumber) return;
    onSave({ holderName, bankName, accountNumber, routingNumber, type: accountType });
    onClose();
    // Reset
    setHolderName("");
    setBankName("");
    setAccountNumber("");
    setRoutingNumber("");
    setAccountType("Checking");
  };

  const handleClose = () => {
    onClose();
    setHolderName("");
    setBankName("");
    setAccountNumber("");
    setRoutingNumber("");
    setAccountType("Checking");
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>Update Bank Account</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2.5, borderRadius: 2 }}>
          Your banking information is encrypted and stored securely.
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Account holder name"
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
              size="small"
              placeholder="As it appears on your bank account"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Bank name"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              size="small"
              placeholder="e.g. Chase Bank"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              label="Account number"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              size="small"
              type="password"
              inputProps={{ maxLength: 17 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              fullWidth
              label="Account type"
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              size="small"
            >
              {ACCOUNT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Routing number"
              value={routingNumber}
              onChange={(e) => setRoutingNumber(e.target.value)}
              size="small"
              type="password"
              inputProps={{ maxLength: 9 }}
              helperText="9-digit ABA routing number"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={!holderName || !bankName || !accountNumber || !routingNumber}
          sx={{ textTransform: "none", backgroundColor: "#178B8E", "&:hover": { backgroundColor: "#146f72" } }}
        >
          Save Bank Account
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateBankAccountDialog;
